import React, { HTMLAttributes, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { generatePath } from 'react-router-dom';
import Card from '@/components/Card/Card';
import CurrencyFormat from 'react-currency-format';
import cn from '@/utils/cn';
import Button from '@/components/Button';
import useHomeContext from '@/hooks/useHomeContext';
import { TWalletData } from '../types/WalletData';
import { routes } from '@/utils/routes';
import { isFeatureEnabled } from '@/config/featureFlags.service';
import { Feature } from '@/config/featureFlags.definition';

const CurrencyClassName = cn(['text-emerge-navy', 'text-3xl', 'font-medium']);

interface AccountCardProps extends HTMLAttributes<HTMLDivElement> {
  data: TWalletData;
  onViewBankAccount?: () => void;
}

const AccountCard: React.FC<AccountCardProps> = ({ data, onViewBankAccount }) => {
  const { setBankAccount, setSelectedAccount, setAccountsFilter } = useHomeContext()!;
  const navigate = useNavigate();
  const [style, setStyle] = useState({ display: 'none' });

  const handleOnClick = () => {
    onViewBankAccount?.();
    setBankAccount(data);
    setSelectedAccount({ label: data.name, value: data.name });
    setAccountsFilter([data.id]);
    if (isFeatureEnabled(Feature.SELECTED_ACCOUNT)) {
      navigate(generatePath(routes.account, { walletId: data.id }));
    }
  };

  return (
    <Card
      className="flex flex-col w-[272px] min-w-[272px] max-w-[272px] h-[130px] min-h-[130px] max-h-[130px] px-4 py-6 border-emerge-navy-4 rounded-2xl"
      onMouseEnter={() => {
        setStyle({ display: 'inherit' });
      }}
      onMouseLeave={() => {
        setStyle({ display: 'none' });
      }}>
      <div className="flex flex-row justify-between items-start gap-2 flex-1">
        <div className="flex flex-col flex-1 min-w-0">
          <h5 className="text-sm font-medium text-emerge-navy truncate">{data.name}</h5>
          <h6 className="text-[10px] font-medium text-emerge-navy-2">{data.accountNumber}</h6>
        </div>
        <div style={style} className="flex-shrink-0 account-card-cta">
          <Button onClick={handleOnClick} variant="tertiary" size="sm">
            View
          </Button>
        </div>
      </div>
      <div className="overflow-hidden mt-auto">
        <CurrencyFormat
          id="currency"
          className={cn(CurrencyClassName, 'truncate')}
          value={Number(data.balance ?? 0).toFixed(2)}
          displayType={'text'}
          thousandSeparator={true}
          prefix={'$'}
          decimalScale={2}
        />
      </div>
    </Card>
  );
};

export default AccountCard;
